import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaChevronDown, FaChevronRight, FaPlus } from "react-icons/fa";
import GenericModal from "components/Modal";
import HeaderButtons from "components/Button/HeaderButtons";
import BackButton from "components/Button/BackButton";
import DeletePopup from "components/DeletePopup/deletePopup";
import Tabs from "components/Tab/Tabs";
import { useToaster } from "contexts/toasterContext";
import { getApiErrorMessage } from "util/errorMessage";
import feedbackManager from "services/Feedback/feedbackManager";
import {
  Feedback,
  flattenReceived,
  formatPeriod,
  getQuarterMonths,
  getWritableQuarters,
  QuarterRef,
  quarterKey,
  ReceivedFeedbackGroup,
  toReceivedGroups,
  UserFeedbackResponse,
} from "services/Feedback/feedbackInterface";
import { FeedbackTarget } from "./ManagerFeedbackForm";
import FeedbackCard from "./components/FeedbackCard";
import { FeedbackEmptyState } from "./components/FeedbackBits";
// Reused rather than re-derived: it already turns a name or a bare email into
// a stable per-person colour and initials.
import { Avatar } from "../ReviewList";
import TruncatedWithTooltip from "components/TruncatedWithTooltip";

/**
 * One author's feedback, collapsible.
 *
 * CHANGE: the group is now a disclosure and starts CLOSED. With every card
 * expanded the tab was a wall of prose — four long comments from one reviewer
 * filled the screen and the reader could not see who else had written
 * anything, which is the question the grouping was added to answer. Closed by
 * default, the tab opens as a short list of contributors with their counts,
 * and the reader chooses whose feedback to read.
 *
 * NOTE: state lives per group, so opening one does not close another — two
 * reviewers' comments are often read side by side.
 *
 * NOTE: a plain row with a rule under it, NOT a bordered card. FeedbackCard is
 * already a bordered box, and wrapping a stack of them in a second box reads
 * as a panel inside a panel. The rule is enough to bind the group together.
 */
const AuthorFeedbackGroup: React.FC<{
  group: ReceivedFeedbackGroup;
  children: React.ReactNode;
}> = ({ group, children }) => {
  const [expanded, setExpanded] = useState(false);

  const author = group.manager;
  // NOTE: the list's own length, never the server's `count`. A number printed
  // beside a list has to come from that list.
  const count = group.feedback.length;
  const subtitle = [author.designation, author.email].filter(Boolean).join(" · ");
  const bodyId = `feedback-group-${author.id}`;

  return (
    /*
     * CHANGE: the group is now a CARD, built from the same parts as the goal
     * sheet cards on the other tab — identity block, a rule, then a disclosure
     * strip. Previously it was a bare row that floated on the page while the
     * Goal sheets tab beside it was a tidy stack of cards, so the two tabs of
     * one screen did not look like the same product.
     *
     * NOTE: the border-in-border this creates once expanded is deliberate and
     * is the app's own pattern — a goal sheet card nests its bordered goal
     * rows the same way.
     */
    <article className="rounded-xl border border-gray-200 bg-white transition-colors dark:border-gray-700 dark:bg-gray-800">
      {/* ── Who wrote them ──────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 px-3.5 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <Avatar name={author.name || author.email} />

          <div className="min-w-0">
            <TruncatedWithTooltip
              text={author.name || author.email}
              className="text-sm font-semibold text-gray-900 dark:text-white"
            />
            {subtitle && (
              <TruncatedWithTooltip
                text={subtitle}
                className="text-xs text-gray-500 dark:text-gray-400"
              />
            )}
          </div>
        </div>
      </div>

      {/* ── Reviews, behind the same strip a goal sheet uses for its rows ── */}
      <div className="border-t border-gray-100 dark:border-gray-700">
        <button
          type="button"
          onClick={() => setExpanded((previous) => !previous)}
          aria-expanded={expanded}
          aria-controls={bodyId}
          className="flex w-full items-center gap-2 px-3.5 py-2 text-xs font-medium text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          {expanded ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
          {count} review{count === 1 ? "" : "s"}
        </button>

        {expanded && (
          <div id={bodyId} className="flex flex-col gap-3 px-3.5 pb-3">
            {children}
          </div>
        )}
      </div>
    </article>
  );
};

export interface EmployeeFeedbackScreenProps {
  /** The person being viewed. Omit for the caller's own screen. */
  target?: FeedbackTarget | null;
  /** Viewer is role 2, 4 or 5 — may add feedback and approve sheets. */
  canReview: boolean;
  /** Viewer's email, used to stop self-approval and to detect "my own screen". */
  viewerEmail: string;
  /** Rendered when viewing someone else, to get back to the list. */
  onBack?: () => void;
}

/** Which tab is open. Drives the `kind` filter sent to GET /user. */
/** CHANGE: "overview" removed — it duplicated the two lists below it. */
type FeedbackTab = "self" | "received";

const TAB_ORDER: FeedbackTab[] = ["self", "received"];

interface ModalState {
  /**
   * CHANGE: "self" and "manager" are both gone — the goal sheet and the
   * reviewer form are routed screens now. Deleting is the only thing left that
   * still belongs in a dialog: it is one sentence and one decision.
   */
  kind: "delete" | null;
  feedback?: Feedback | null;
}

const CLOSED_MODAL: ModalState = { kind: null };

const EmployeeFeedbackScreen: React.FC<EmployeeFeedbackScreenProps> = ({
  target,
  canReview,
  viewerEmail,
  onBack,
}) => {
  const { addToast } = useToaster();

  const [data, setData] = useState<UserFeedbackResponse | null>(null);
  /**
   * null = "whatever year the server defaults to".
   *
   * NOTE: kept separate from the year the server reports rather than seeding
   * this from the response. Writing the response's year back into the state
   * the fetch depends on would re-trigger the fetch, so every screen load
   * would hit the API twice.
   */
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<FeedbackTab>("self");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [modal, setModal] = useState<ModalState>(CLOSED_MODAL);
  const [deleting, setDeleting] = useState(false);
  /** Id of the feedback whose share toggle is in flight, so only it disables. */
  const [sharingId, setSharingId] = useState<number | null>(null);

  const navigate = useNavigate();

  const summary = data?.summary;

  // Own screen when no target is given, or the target resolves to the viewer.
  const isOwnScreen = useMemo(() => {
    if (!target) return true;
    if (!summary?.email) return false;
    return summary.email.toLowerCase() === viewerEmail.toLowerCase();
  }, [target, summary?.email, viewerEmail]);

  // ── Load ──────────────────────────────────────────────────────────────────

  const loadScreen = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      // No `kind` — one call fills both lists, so switching tabs needs no refetch.
      const response = await feedbackManager.getUserFeedback({
        userId: target?.userId,
        year: selectedYear ?? undefined,
      });
      setData(response.data);
    } catch (error) {
      setLoadError(getApiErrorMessage(error, "Failed to load feedback."));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [target?.userId, selectedYear]);

  useEffect(() => {
    void loadScreen();
  }, [loadScreen]);

  // ── Derived ───────────────────────────────────────────────────────────────

  /**
   * Quarters the employee may still start a sheet for: the current and the
   * previous one, minus any that already exist. The server allows only these
   * two, so offering more would guarantee a 400.
   */
  const openQuarters = useMemo<QuarterRef[]>(() => {
    if (!isOwnScreen || !data) return [];
    const taken = new Set(
      data.self.map((item) => quarterKey({ year: item.year, quarter: item.quarter })),
    );
    return getWritableQuarters().filter((quarter) => !taken.has(quarterKey(quarter)));
  }, [isOwnScreen, data]);

  /** Nobody approves their own sheet — any role, admins included. */
  const canApproveHere = canReview && !isOwnScreen;

  // ── Actions ───────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    const feedback = modal.feedback;
    if (!feedback) return;

    setDeleting(true);
    try {
      if (feedback.kind === "SELF") {
        await feedbackManager.deleteSelfFeedback(feedback.id);
      } else {
        await feedbackManager.deleteManagerFeedback(feedback.id);
      }
      addToast("Feedback deleted successfully", "success");
      setModal(CLOSED_MODAL);
      void loadScreen();
    } catch (error) {
      addToast(getApiErrorMessage(error, "Failed to delete feedback"), "error");
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Share manager feedback with the employee, or make it private again.
   *
   * CHANGE: new. Manager feedback is PRIVATE by default under the updated
   * contract, so without this an author could write feedback the employee
   * would never see and have no way to release it.
   *
   * NOTE: PUT /employee/{id} REPLACES the record, so the whole body goes up
   * with only the flag changed — a patch-shaped body would blank the comment
   * and the rows. `year`/`quarter` are deliberately omitted: left out they keep
   * the feedback's own quarter, and an approving record is forbidden to change
   * it at all.
   */
  const handleToggleShare = async (feedback: Feedback, next: boolean) => {
    setSharingId(feedback.id);
    try {
      await feedbackManager.updateManagerFeedback(feedback.id, {
        overallComment: feedback.overallComment ?? "",
        overallRating: feedback.overallRating,
        items: feedback.items.map((item) => ({
          areaId: item.areaId,
          objectiveId: item.objectiveId,
          weightage: item.weightage,
          achievement: item.achievement,
          rating: item.rating,
        })),
        visibleToEmployee: next,
      });
      addToast(
        next ? "Feedback shared with the employee" : "Feedback is private again",
        "success",
      );
      void loadScreen();
    } catch (error) {
      addToast(getApiErrorMessage(error, "Failed to change who can see this"), "error");
    } finally {
      setSharingId(null);
    }
  };

  /**
   * Everything the reviewer screens need to render without a refetch, and to
   * come back to this employee rather than the bare roll-up.
   */
  const reviewerState = (feedback?: Feedback) => ({
    state: {
      ...(feedback ? { feedback } : {}),
      ...(summary ? { employee: { userId: summary.userId, name: summary.name } } : {}),
    },
  });

  /**
   * Jumping between a sheet and its approval. Both are already loaded, so this
   * only switches tab — no fetch, and no route change to lose scroll position.
   */
  const handleOpenLinked = (feedbackId: number) => {
    const inSelf = data?.self.some((item) => item.id === feedbackId);
    setActiveTab(inSelf ? "self" : "received");
  };

  /** Delete is offered only where the server would actually allow it. */
  const canDelete = (feedback: Feedback): boolean => {
    if (!feedback.editable) return false;
    // An approving record can't be deleted — the contract says edit it instead.
    if (feedback.kind === "MANAGER" && feedback.selfFeedbackId !== null) return false;
    return true;
  };

  /**
   * CHANGE: extracted from renderCards so the grouped Feedback tab and the flat
   * Goal sheets tab render an identical card. The two lists now have different
   * SHAPES but must not grow different behaviour.
   */
  const renderCard = (item: Feedback, expanded: boolean) => (
    <FeedbackCard
      key={`${item.kind}-${item.id}`}
      feedback={item}
      defaultExpanded={expanded}
      onOpenLinked={handleOpenLinked}
      onEdit={
        item.editable
          ? (feedback) =>
            feedback.kind === "SELF"
              ? // CHANGE: the goal sheet opens as a screen. The record
              // rides along in router state so the editor needs no
              // second fetch; it falls back to GET /{id} on refresh.
              navigate(`/admin/feedback/goal-sheet/${feedback.id}`, {
                state: { feedback },
              })
              : navigate(`/admin/feedback/review/${feedback.id}`, reviewerState(feedback))
          : undefined
      }
      onDelete={
        canDelete(item) ? (feedback) => setModal({ kind: "delete", feedback }) : undefined
      }
      // Only the author or an admin may share, which is exactly what the
      // server reports through `editable` on a MANAGER record.
      onToggleShare={
        item.kind === "MANAGER" && item.editable ? handleToggleShare : undefined
      }
      sharing={sharingId === item.id}
      /* CHANGE: on an APPROVED sheet, hand the card the reviewer's own
         record so each goal shows the employee's entry and the
         reviewer's side by side. `approvalFeedbackId` is null when this
         viewer may not see it, so the comparison stays hidden rather
         than half-rendered. */
      reviewerFeedback={
        item.kind === "SELF" && item.approvalFeedbackId !== null
          ? // Searched across EVERY author's feedback: the approval can
          // have been written by anyone who may review, so looking only
          // inside one group would silently drop the comparison.
          receivedAll.find((entry) => entry.id === item.approvalFeedbackId) ?? null
          : null
      }
      onApprove={
        canApproveHere
          ? (feedback) =>
            navigate(
              `/admin/feedback/review/approve/${feedback.id}`,
              reviewerState(feedback),
            )
          : undefined
      }
    />
  );

  const renderCards = (items: Feedback[], emptyTitle: string, emptyDescription: string) => {
    if (items.length === 0) {
      return <FeedbackEmptyState title={emptyTitle} description={emptyDescription} />;
    }

    return (
      <div className="flex flex-col gap-3">
        {items.map((item) => renderCard(item, items.length === 1))}
      </div>
    );
  };

  /**
   * The Feedback tab — one section per author.
   *
   * NOTE: the section header is shown even when a single person wrote
   * everything. Naming the author and their count IS the information this
   * grouping exists to convey, and hiding it in the common case would mean the
   * tab changed shape depending on the data.
   *
   * NOTE: `group.feedback.length` drives the count, not the server's `count`.
   * They should agree, but the number beside a list must come from the list.
   */
  const renderReceivedGroups = (groups: ReceivedFeedbackGroup[], totalItems: number) => {
    if (totalItems === 0) {
      return (
        <FeedbackEmptyState
          title="No feedback received"
          description="Feedback written by managers and admins appears here, including approval comments."
        />
      );
    }

    return (
      <div className="flex flex-col gap-3">
        {groups
          .filter((group) => (group.feedback?.length ?? 0) > 0)
          .map((group) => (
            <AuthorFeedbackGroup key={group.manager.id} group={group}>
              {group.feedback.map((item) => renderCard(item, totalItems === 1))}
            </AuthorFeedbackGroup>
          ))}
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────

  /*
   * CHANGE: the first load no longer replaces the whole screen.
   *
   * THE BUG THIS FIXES: `loading` starts true, so the very first render
   * returned ONLY a centred spinner — no name, no year picker, no tab bar.
   * On a reload that reads as a blank page for as long as the request takes,
   * and it is why this screen felt unlike Employees, Leave or Reimbursement:
   * those render through DataTable, which puts its toolbar and table head up
   * immediately and shows the loading state in the rows alone. The page
   * structure is there from the first frame; only the data arrives later.
   *
   * The header and tabs now render straight away — `target.name` is already
   * known from the list or the restored history state, so there is something
   * real to show — and the placeholder lives inside the tab content.
   */
  const isInitialLoad = loading && !data;
  const hasLoadFailed = Boolean(loadError) && !data;

  /**
   * Skeleton cards rather than a spinner: they occupy the shape the content
   * will take, so the layout does not jump when the data lands. Same idiom as
   * the notification panel's loading rows.
   */
  const loadingPlaceholder = (
    <div className="flex flex-col gap-3" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading feedback…</span>
      {[0, 1].map((row) => (
        <div
          key={row}
          className="rounded-xl border border-gray-200 bg-white px-3.5 py-3 dark:border-gray-700 dark:bg-gray-800"
        >
          <div className="h-3.5 w-40 animate-pulse rounded bg-gray-100 dark:bg-gray-700" />
          <div className="mt-2 h-2.5 w-64 animate-pulse rounded bg-gray-100 dark:bg-gray-700" />
          <div className="mt-3 h-2.5 w-28 animate-pulse rounded bg-gray-100 dark:bg-gray-700" />
        </div>
      ))}
    </div>
  );

  /**
   * One gate for every tab, so loading and failure look the same in both
   * instead of each tab inventing its own. Written as statements rather than
   * nested ternaries (Sonar).
   */
  const tabContent = (loaded: React.ReactNode): React.ReactNode => {
    if (isInitialLoad) return loadingPlaceholder;
    if (hasLoadFailed) {
      return (
        <div className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-error dark:bg-red-900/20">
          {loadError}
        </div>
      );
    }
    return loaded;
  };

  /** No count until there is one — "(0)" while loading is a lie. */
  const tabLabel = (base: string, count: number) =>
    isInitialLoad ? base : `${base} (${count})`;

  const selfList = data?.self ?? [];
  /* CHANGE: `received` arrives grouped by author. `toReceivedGroups` also
     accepts the previous flat array, so a front end deployed ahead of the API
     degrades to the same grouping instead of a blank tab. */
  const receivedGroups = toReceivedGroups(data?.received);
  const receivedAll = flattenReceived(receivedGroups);
  const pendingCount = summary?.pendingReviewCount ?? 0;

  const tabs = [
    {
      label: tabLabel("Goal sheets", selfList.length),
      // CHANGE: the pending-approval dot moved here from Overview. A reviewer's
      // attention belongs on the sheet that is waiting for them, and that sheet
      // lives in this tab.
      showIndicator: canApproveHere && pendingCount > 0,
      content: tabContent(
        renderCards(
          selfList,
          "No goal sheets",
          isOwnScreen
            ? "Create one for the current or previous quarter."
            : "This employee has not submitted a goal sheet for the selected year.",
        ),
      ),
    },
    {
      // CHANGE: "Feedback received" -> "Feedback". The tab already sits next
      // to "Goal sheets" on a screen about one person, so "received" only
      // repeated what the position already says.
      // The count is every record across all authors, not the number of
      // authors — it answers "how much feedback", which is what a reader of a
      // tab label expects.
      label: tabLabel("Reviews", receivedAll.length),
      content: tabContent(renderReceivedGroups(receivedGroups, receivedAll.length)),
    },
  ];

  return (
    /* CHANGE: a three-region column — header, tab bar, scrolling content —
       instead of one growing block. The identity, year picker and actions now
       stay put while only the cards move, which is what makes a long list of
       reviews readable without losing track of whose screen you are on. */
    <div className="flex h-full min-h-0 flex-col gap-3">
      {/* ── Header: pinned ──────────────────────────────────────────────── */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 items-center gap-2.5">
          {onBack && <BackButton onClick={onBack} label="Back to the feedback list" />}
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-gray-900 dark:text-white">
              {isOwnScreen ? "My Reviews" : summary?.name || target?.name || "Employee"}
            </h2>
            <p className="truncate text-xs text-gray-500 dark:text-gray-400">
              {summary?.designation ? `${summary.designation} · ` : ""}
              {summary?.email}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Year dropdown — the API returns the selectable years. */}
          {data && data.years.length > 0 && (
            <select
              value={selectedYear ?? data.summary.year}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              aria-label="Review year"
              className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-primary focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              {data.years.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          )}

          {/* Own screen: start a sheet for any quarter still open. */}
          {isOwnScreen &&
            openQuarters.map((quarter) => (
              <HeaderButtons
                key={quarterKey(quarter)}
                type="button"
                variant="primary"
                size="small"
                icon={<FaPlus size={11} />}
                onClick={() =>
                  navigate(`/admin/feedback/goal-sheet/new/${quarterKey(quarter)}`)
                }
              >
                {/* Parenthesised rather than a third "·" segment: the label
                    already uses one separator, and a second made the three
                    parts read as equals instead of label · period (hint). */}
                {`Goal sheet · ${formatPeriod(quarter)}${getQuarterMonths(quarter.quarter)
                  ? ` (${getQuarterMonths(quarter.quarter)})`
                  : ""
                  }`}
              </HeaderButtons>
            ))}

          {/* Reviewer on someone else's screen: write feedback unprompted. */}
          {canApproveHere && summary && (
            <HeaderButtons
              type="button"
              variant="primary"
              size="small"
              icon={<FaPlus size={11} />}
              onClick={() =>
                navigate(`/admin/feedback/review/new/${summary.userId}`, reviewerState())
              }
            >
              Add Review
            </HeaderButtons>
          )}
        </div>
      </div>

      {loadError && (
        <div className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-error dark:bg-red-900/20">
          {loadError}
        </div>
      )}

      {/* Tabs is already `h-full flex flex-col` with a sticky bar and a
          `flex-1 min-h-0` content area, so it only needed a definite height to
          work against. `overflow-y-auto` on the content is what keeps the bar
          in place while the cards scroll beneath it. */}
      <div className="min-h-0 flex-1">
        <Tabs
          tabs={tabs}
          activeIndex={TAB_ORDER.indexOf(activeTab)}
          onTabChange={(index) => setActiveTab(TAB_ORDER[index])}
          contentClassName="pt-3 overflow-y-auto"
        />
      </div>

      {/* ── Modals ──────────────────────────────────────────────────────── */}
      {modal.kind === "delete" && modal.feedback && (
        <GenericModal isOpen onClose={() => setModal(CLOSED_MODAL)} header="Delete Review">
          <DeletePopup
            handleDelete={handleDelete}
            onClose={() => setModal(CLOSED_MODAL)}
            selectedRow={modal.feedback}
            message={
              deleting
                ? "Deleting…"
                : `Delete the ${modal.feedback.period} ${modal.feedback.kind === "SELF" ? "goal sheet" : "review"
                }? This can't be undone.`
            }
          />
        </GenericModal>
      )}
    </div>
  );
};

export default EmployeeFeedbackScreen;
